const isRenderedFacetElement = (element, mobileViewport) =>
  Boolean(
    element &&
      !element.disabled &&
      !element.hidden &&
      !element.closest?.("[hidden], [inert], .hidden") &&
      !(mobileViewport && element.closest?.(".small-hide")) &&
      !(!mobileViewport && element.closest?.(".medium-hide, .large-up-hide")),
  );

const isAvailableFacetFocusTarget = (element) => {
  const mobileViewport = window.matchMedia("(max-width: 749px)").matches;
  if (!isRenderedFacetElement(element, mobileViewport)) return false;

  const drawerSummaryContent = element.matches?.(
    "menu-drawer > details > summary",
  )
    ? element.querySelector?.(".mobile-facets__open")
    : null;
  return (
    !drawerSummaryContent ||
    isRenderedFacetElement(drawerSummaryContent, mobileViewport)
  );
};

const findAvailableFacetFocusTarget = (selector) =>
  Array.from(document.querySelectorAll(selector)).find(
    isAvailableFacetFocusTarget,
  );

const findCurrentFacetFocusFallback = () =>
  findAvailableFacetFocusTarget(
    "#FacetFiltersForm .js-filter > summary, #FacetFiltersForm select, #FacetSortDrawerForm select, menu-drawer > details > summary",
  );

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    window.addEventListener("popstate", FacetFiltersForm.onHistoryChange);
  }

  static initializeHistoryState() {
    const state = history.state || {};
    const historyPosition = Number.isInteger(
      state[FacetFiltersForm.historyPositionKey],
    )
      ? state[FacetFiltersForm.historyPositionKey]
      : 0;
    FacetFiltersForm.currentHistoryPosition = historyPosition;
    FacetFiltersForm.successfulHistoryPosition = historyPosition;
    history.replaceState(
      {
        ...state,
        searchParams: FacetFiltersForm.searchParamsInitial,
        [FacetFiltersForm.historyPositionKey]: historyPosition,
      },
      "",
    );
  }

  static onHistoryChange(event) {
    const state = event.state || {};
    const historyPosition = Number.isInteger(
      state[FacetFiltersForm.historyPositionKey],
    )
      ? state[FacetFiltersForm.historyPositionKey]
      : 0;
    FacetFiltersForm.currentHistoryPosition = historyPosition;

    if (FacetFiltersForm.historyRollbackInProgress) {
      FacetFiltersForm.historyRollbackInProgress = false;
      if (historyPosition === FacetFiltersForm.successfulHistoryPosition)
        return;
    }

    const searchParams = event.state
      ? event.state.searchParams
      : FacetFiltersForm.searchParamsInitial;
    if (searchParams === FacetFiltersForm.searchParamsPrev) {
      FacetFiltersForm.cancelActiveRequest();
      FacetFiltersForm.successfulHistoryPosition = historyPosition;
      return;
    }
    FacetFiltersForm.renderPage(searchParams, null, false, historyPosition);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(
    searchParams,
    event,
    updateURLHash = true,
    historyPosition = FacetFiltersForm.currentHistoryPosition,
  ) {
    const requestGeneration = ++FacetFiltersForm.requestGeneration;
    const requestState = {
      historyPosition,
      searchParams,
      previousSearchParams: FacetFiltersForm.searchParamsPrev,
      updateURLHash,
    };
    const sections = FacetFiltersForm.getSections();
    const focusState = FacetFiltersForm.captureFocusState();
    FacetFiltersForm.activeRequest = {
      focusState,
      requestGeneration,
      requestState,
    };
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop",
    );
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }
    FacetFiltersForm.setBusyState(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(
            filterDataUrl,
            event,
            focusState,
            requestGeneration,
            requestState,
          )
        : FacetFiltersForm.renderSectionFromFetch(
            url,
            event,
            focusState,
            requestGeneration,
            requestState,
          );
    });
  }

  static renderSectionFromFetch(
    url,
    event,
    focusState,
    requestGeneration,
    requestState,
  ) {
    return fetch(url)
      .then((response) => {
        if (!response.ok)
          throw new Error(
            `Facet request failed with status ${response.status}.`,
          );
        return response.text();
      })
      .then((responseText) => {
        if (requestGeneration !== FacetFiltersForm.requestGeneration) return;
        const html = responseText;
        if (!FacetFiltersForm.isRenderableSection(html, event)) {
          throw new Error(
            "Facet response did not contain a renderable product section.",
          );
        }
        FacetFiltersForm.renderSection(
          html,
          event,
          focusState,
          requestGeneration,
          requestState,
        );
        if (requestGeneration === FacetFiltersForm.requestGeneration) {
          FacetFiltersForm.filterData = [
            ...FacetFiltersForm.filterData,
            { html, url },
          ];
        }
      })
      .catch((error) => {
        FacetFiltersForm.handleRenderFailure(
          error,
          focusState,
          requestGeneration,
          requestState,
        );
      });
  }

  static renderSectionFromCache(
    filterDataUrl,
    event,
    focusState,
    requestGeneration,
    requestState,
  ) {
    if (requestGeneration !== FacetFiltersForm.requestGeneration) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    try {
      FacetFiltersForm.renderSection(
        html,
        event,
        focusState,
        requestGeneration,
        requestState,
      );
    } catch (error) {
      FacetFiltersForm.handleRenderFailure(
        error,
        focusState,
        requestGeneration,
        requestState,
      );
    }
  }

  static renderSection(
    html,
    event,
    focusState,
    requestGeneration,
    requestState,
  ) {
    if (requestGeneration !== FacetFiltersForm.requestGeneration) return;
    const renderPlan = FacetFiltersForm.prepareSectionRender(html, event);
    FacetFiltersForm.renderFilters(renderPlan);
    FacetFiltersForm.renderProductGridContainer(renderPlan);
    FacetFiltersForm.renderProductCount(renderPlan);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
    if (requestGeneration === FacetFiltersForm.requestGeneration) {
      FacetFiltersForm.commitSearchState(requestState);
      FacetFiltersForm.settleRequestState(focusState, requestGeneration);
    }
  }

  static parseSectionHTML(html) {
    return typeof html === "string"
      ? new DOMParser().parseFromString(html, "text/html")
      : html;
  }

  static prepareSectionRender(html, event) {
    const parsedHTML = FacetFiltersForm.parseSectionHTML(html);
    const productGrid = {
      source: parsedHTML?.getElementById("ProductGridContainer"),
      target: document.getElementById("ProductGridContainer"),
    };
    const productCount = {
      source: parsedHTML?.getElementById("ProductCount"),
      target: document.getElementById("ProductCount"),
    };
    const requiredElements = [productGrid, productCount];

    [
      "FacetFiltersForm",
      "FacetFiltersFormMobile",
      "FacetFiltersPillsForm",
      "FacetSortDrawerForm",
    ].forEach((id) => {
      const source = parsedHTML?.getElementById(id);
      const target = document.getElementById(id);
      if (source || target) requiredElements.push({ source, target });
    });

    const facetSelector =
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter";
    const sourceFacets = Array.from(
      parsedHTML?.querySelectorAll(facetSelector) || [],
    );
    const targetFacets = Array.from(document.querySelectorAll(facetSelector));
    const targetFacetsByIndex = new Map(
      targetFacets.map((target) => [target.dataset.index, target]),
    );
    const selectedFilter = event?.target?.closest?.(".js-filter");
    const allFacetPairs = sourceFacets.map((source) => ({
      source,
      target: targetFacetsByIndex.get(source.dataset.index),
    }));
    const facetPairs = allFacetPairs.filter(
      ({ source }) =>
        !selectedFilter ||
        source.dataset.index !== selectedFilter.dataset.index,
    );
    requiredElements.push(...allFacetPairs);
    if (sourceFacets.length !== targetFacets.length) {
      requiredElements.push({ source: sourceFacets[0] || null, target: null });
    }

    const stagedElementPairs = [
      ".active-facets-mobile",
      ".active-facets-desktop",
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ]
      .map((selector) => ({
        selector,
        source: parsedHTML?.querySelector(selector),
        target: document.querySelector(selector),
      }))
      .filter(({ source, target }) => source || target);
    requiredElements.push(...stagedElementPairs);

    const mobileForm = document.getElementById("FacetFiltersFormMobile");
    let mobileDrawer = null;
    if (mobileForm) {
      mobileDrawer = mobileForm.closest("menu-drawer");
      requiredElements.push({
        source: parsedHTML?.getElementById("FacetFiltersFormMobile"),
        target:
          typeof mobileDrawer?.bindEvents === "function" ? mobileDrawer : null,
      });
    }

    const selectedSource = sourceFacets.find(
      (source) =>
        selectedFilter && source.dataset.index === selectedFilter.dataset.index,
    );
    const stagedCountUpdates = selectedSource
      ? [".facets__selected", ".facets__summary"].map((selector) => ({
          source: selectedSource.querySelector(selector),
          target: selectedFilter.querySelector(selector),
        }))
      : [];
    requiredElements.push(
      ...stagedCountUpdates.filter(({ source, target }) => source || target),
    );

    if (requiredElements.some(({ source, target }) => !source || !target)) {
      throw new Error(
        "Facet response did not contain a renderable product section.",
      );
    }

    return {
      activeFacetPairs: stagedElementPairs.filter(({ selector }) =>
        selector.startsWith(".active-facets"),
      ),
      additionalElementPairs: stagedElementPairs.filter(
        ({ selector }) => !selector.startsWith(".active-facets"),
      ),
      countUpdates: stagedCountUpdates.filter(
        ({ source, target }) => source && target,
      ),
      facetPairs,
      mobileDrawer,
      productCount,
      productCountDesktop: document.getElementById("ProductCountDesktop"),
      productGrid,
    };
  }

  static isRenderableSection(html, event) {
    try {
      FacetFiltersForm.prepareSectionRender(html, event);
      return true;
    } catch (_error) {
      return false;
    }
  }

  static commitSearchState(requestState) {
    if (!requestState) return;
    FacetFiltersForm.searchParamsPrev = requestState.searchParams;
    if (requestState.updateURLHash) {
      FacetFiltersForm.updateURLHash(requestState.searchParams);
    } else {
      FacetFiltersForm.currentHistoryPosition = requestState.historyPosition;
      FacetFiltersForm.successfulHistoryPosition = requestState.historyPosition;
    }
  }

  static handleRenderFailure(
    error,
    focusState,
    requestGeneration,
    requestState,
  ) {
    if (requestGeneration !== FacetFiltersForm.requestGeneration) return;
    FacetFiltersForm.restoreSearchState(requestState);
    FacetFiltersForm.settleRequestState(focusState, requestGeneration);
    console.error(error);
  }

  static cancelActiveRequest() {
    const activeRequest = FacetFiltersForm.activeRequest;
    if (
      !activeRequest ||
      activeRequest.requestGeneration !== FacetFiltersForm.requestGeneration
    )
      return;
    FacetFiltersForm.requestGeneration += 1;
    FacetFiltersForm.activeRequest = null;
    FacetFiltersForm.restoreSearchState({
      ...activeRequest.requestState,
      previousSearchParams: FacetFiltersForm.searchParamsPrev,
      updateURLHash: true,
    });
    FacetFiltersForm.settleRenderState(activeRequest.focusState);
  }

  static settleRequestState(focusState, requestGeneration) {
    if (requestGeneration !== FacetFiltersForm.requestGeneration) return;
    try {
      FacetFiltersForm.settleRenderState(focusState);
    } finally {
      if (
        FacetFiltersForm.activeRequest?.requestGeneration === requestGeneration
      ) {
        FacetFiltersForm.activeRequest = null;
      }
    }
  }

  static restoreSearchState(requestState) {
    if (!requestState) return;
    const searchParams = requestState.previousSearchParams || "";
    const params = new URLSearchParams(searchParams);

    document.querySelectorAll("facet-filters-form form").forEach((form) => {
      Array.from(form.elements || []).forEach((control) => {
        if (!control.name) return;
        const values = params.getAll(control.name);
        if (control.type === "checkbox" || control.type === "radio") {
          control.checked = values.includes(control.value);
        } else if (control.tagName === "SELECT") {
          const defaultOption = Array.from(control.options || []).find(
            (option) => option.defaultSelected,
          );
          control.value =
            values.length > 0
              ? values[values.length - 1]
              : defaultOption?.value || "";
        } else if (control.type === "number" || control.type === "range") {
          control.value = values.length > 0 ? values[values.length - 1] : "";
        }
      });
    });

    document
      .querySelectorAll("facet-filters-form price-range")
      .forEach((priceRange) => {
        priceRange.setMinAndMaxValues?.();
      });

    if (!requestState.updateURLHash) {
      FacetFiltersForm.rollbackHistory(requestState.historyPosition);
    }
  }

  static rollbackHistory(failedHistoryPosition) {
    const historyDelta =
      FacetFiltersForm.successfulHistoryPosition - failedHistoryPosition;
    if (historyDelta === 0 || FacetFiltersForm.historyRollbackInProgress)
      return;
    if (typeof history.go !== "function") return;
    FacetFiltersForm.historyRollbackInProgress = true;
    history.go(historyDelta);
  }

  static settleRenderState(focusState) {
    document
      .getElementById("ProductGridContainer")
      ?.querySelector(".collection")
      ?.classList.remove("loading");
    ["ProductCount", "ProductCountDesktop"].forEach((id) => {
      document.getElementById(id)?.classList.remove("loading");
    });
    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.restoreFocusState(focusState);
    FacetFiltersForm.setBusyState(false);
  }

  static setBusyState(isBusy) {
    ["ProductGridContainer", "ProductCount", "ProductCountDesktop"].forEach(
      (id) => {
        const element = document.getElementById(id);
        if (!element) return;
        if (isBusy) element.setAttribute("aria-busy", "true");
        else element.removeAttribute("aria-busy");
      },
    );
  }

  static captureFocusState() {
    const element = document.activeElement;
    if (!element?.closest?.("facet-filters-form, menu-drawer, .active-facets"))
      return null;

    const mobile = Boolean(
      element.closest("menu-drawer, .active-facets-mobile"),
    );
    const activeFacets = element.closest(
      ".active-facets-mobile, .active-facets-desktop",
    );
    if (activeFacets && element.matches?.("a[href]")) {
      const links = Array.from(activeFacets.querySelectorAll("a[href]"));
      return {
        element,
        type: "active-facet",
        mobile,
        formId: element.closest("form")?.id || "",
        containerClass: activeFacets.classList.contains("active-facets-mobile")
          ? "active-facets-mobile"
          : "active-facets-desktop",
        href: element.getAttribute("href") || "",
        index: links.indexOf(element),
      };
    }

    const filter = element.closest(".js-filter");
    if (filter && element.matches?.("summary")) {
      return {
        element,
        type: "filter-summary",
        mobile,
        formId: element.closest("form")?.id || "",
        filterIndex: filter.dataset.index || "",
      };
    }

    if (element.id) {
      return {
        element,
        type: "id",
        mobile,
        formId: element.closest("form")?.id || "",
        id: element.id,
      };
    }

    if (filter) {
      return {
        element,
        type: "filter-summary",
        mobile,
        formId: element.closest("form")?.id || "",
        filterIndex: filter.dataset.index || "",
      };
    }

    if (element.matches?.("menu-drawer > details > summary")) {
      return { element, type: "drawer-summary", mobile: true };
    }

    return null;
  }

  static restoreFocusState(state) {
    if (!state) return;

    const activeElement = document.activeElement;
    if (
      activeElement &&
      activeElement !== document.body &&
      activeElement.isConnected &&
      isAvailableFacetFocusTarget(activeElement)
    )
      return;

    let target = null;
    if (state.type === "active-facet") {
      const root = document.getElementById(state.formId) || document;
      const container = root.querySelector(`.${state.containerClass}`);
      const links = Array.from(container?.querySelectorAll("a[href]") || []);
      target =
        links.find((link) => link.getAttribute("href") === state.href) ||
        links[Math.min(Math.max(state.index, 0), links.length - 1)];
    } else if (state.type === "filter-summary") {
      const form = document.getElementById(state.formId);
      const filter = Array.from(
        form?.querySelectorAll(".js-filter") || [],
      ).find((element) => element.dataset.index === state.filterIndex);
      target = filter?.querySelector(":scope > summary");
    } else if (state.type === "id") {
      const form = document.getElementById(state.formId);
      target = Array.from(form?.querySelectorAll("[id]") || []).find(
        (element) => element.id === state.id,
      );
    } else if (state.type === "drawer-summary") {
      target = document.querySelector("menu-drawer > details > summary");
    }

    if (!isAvailableFacetFocusTarget(target)) {
      target = findCurrentFacetFocusFallback();
    }
    target?.focus();
  }

  static renderProductGridContainer(renderPlan) {
    renderPlan.productGrid.target.innerHTML =
      renderPlan.productGrid.source.innerHTML;

    renderPlan.productGrid.target
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(renderPlan) {
    const count = renderPlan.productCount.source.innerHTML;
    const container = renderPlan.productCount.target;
    const containerDesktop = renderPlan.productCountDesktop;
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(renderPlan) {
    renderPlan.facetPairs.forEach(({ source, target }) => {
      target.innerHTML = source.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(renderPlan.activeFacetPairs);
    FacetFiltersForm.renderAdditionalElements(
      renderPlan.additionalElementPairs,
      renderPlan.mobileDrawer,
    );

    renderPlan.countUpdates.forEach(({ source, target }) => {
      target.outerHTML = source.outerHTML;
    });
  }

  static renderActiveFacets(activeFacetPairs) {
    activeFacetPairs.forEach(({ source, target }) => {
      target.innerHTML = source.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(additionalElementPairs, mobileDrawer) {
    additionalElementPairs.forEach(({ source, target }) => {
      target.innerHTML = source.innerHTML;
    });

    mobileDrawer?.bindEvents();
  }

  static updateURLHash(searchParams) {
    const historyPosition = FacetFiltersForm.currentHistoryPosition + 1;
    history.pushState(
      {
        ...(history.state || {}),
        searchParams,
        [FacetFiltersForm.historyPositionKey]: historyPosition,
      },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
    FacetFiltersForm.currentHistoryPosition = historyPosition;
    FacetFiltersForm.successfulHistoryPosition = historyPosition;
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form"),
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm"
          ) {
            const noJsElements = document.querySelectorAll(".no-js-list");
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestGeneration = 0;
FacetFiltersForm.activeRequest = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.historyPositionKey = "facetFiltersPosition";
FacetFiltersForm.currentHistoryPosition = 0;
FacetFiltersForm.successfulHistoryPosition = 0;
FacetFiltersForm.historyRollbackInProgress = false;
FacetFiltersForm.initializeHistoryState();
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class MobileFacetDrawer extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
  }

  bindEvents() {
    if (this.isBound) return;
    this.isBound = true;
    this.details = this.querySelector(":scope > details");
    this.summary = this.details?.querySelector(":scope > summary");
    if (!this.details || !this.summary) return;

    this.summary.setAttribute(
      "aria-expanded",
      this.details.open ? "true" : "false",
    );
    this.details.addEventListener("toggle", () => this.syncDrawerState());
    this.addEventListener("click", (event) => {
      if (event.target.closest("[data-mobile-facets-close]")) {
        this.close(true);
        return;
      }

      const submenuClose = event.target.closest(".mobile-facets__close-button");
      if (submenuClose) {
        const submenuDetails = submenuClose.closest(".mobile-facets__details");
        this.closeSubmenu(submenuDetails, true);
      }
    });
    this.addEventListener("keydown", (event) => this.onKeydown(event));
    this.querySelectorAll(".mobile-facets__details").forEach((details) => {
      details.addEventListener("toggle", () => this.syncSubmenuState(details));
    });
    this.mobileBreakpoint = window.matchMedia("(max-width: 749px)");
    this.mobileBreakpoint.addEventListener("change", (event) =>
      this.reconcileBreakpoint(event),
    );
    this.syncDrawerState(false);
  }

  reconcileBreakpoint(event) {
    const activeElement = document.activeElement;
    const closesResponsiveDrawer =
      !event.matches &&
      this.details.open &&
      !isAvailableFacetFocusTarget(this.summary);
    if (
      !activeElement?.closest?.(
        "facet-filters-form, menu-drawer, .active-facets",
      )
    ) {
      if (closesResponsiveDrawer) this.close(false);
      return;
    }

    const focusTarget = !isAvailableFacetFocusTarget(activeElement)
      ? closesResponsiveDrawer && this.contains(activeElement)
        ? this.getDesktopFocusTarget(activeElement)
        : findCurrentFacetFocusFallback()
      : null;

    if (closesResponsiveDrawer) this.close(false);
    focusTarget?.focus();
  }

  getDesktopFocusTarget(activeElement) {
    const mobileId = activeElement?.id || "";
    const desktopId =
      mobileId === "SortBy-mobile"
        ? "SortBy"
        : mobileId
            .replace("Mobile-Filter-", "Filter-")
            .replace("-mobile-", "-");
    let target =
      desktopId && desktopId !== mobileId
        ? document.getElementById(desktopId)
        : null;

    if (!isAvailableFacetFocusTarget(target)) target = null;
    if (target) return target;

    const mobileFilter = activeElement?.closest?.(".js-filter");
    const desktopIndex = mobileFilter?.dataset.index?.replace(/^mobile-/, "");
    if (desktopIndex) {
      const desktopFilter = Array.from(
        document.querySelectorAll("#FacetFiltersForm .js-filter"),
      ).find((filter) => filter.dataset.index === desktopIndex);
      target = desktopFilter?.querySelector(":scope > summary");
    }

    return isAvailableFacetFocusTarget(target)
      ? target
      : findCurrentFacetFocusFallback();
  }

  syncDrawerState(returnFocus = true) {
    const isOpen = this.details.open;
    const focusWasInside =
      this.contains(document.activeElement) &&
      document.activeElement !== this.summary;
    this.details.classList.toggle("menu-opening", isOpen);
    this.summary.setAttribute("aria-expanded", isOpen ? "true" : "false");

    const form = this.details.querySelector("facet-filters-form");
    if (form) form.inert = !isOpen;
    if (isOpen) {
      if (
        returnFocus &&
        (!this.contains(document.activeElement) ||
          document.activeElement === this.summary)
      ) {
        this.getFocusableElements()[0]?.focus();
      }
      return;
    }

    this.querySelectorAll(".mobile-facets__details[open]").forEach((details) =>
      this.closeSubmenu(details),
    );
    if (returnFocus && focusWasInside) this.summary.focus();
  }

  syncSubmenuState(details) {
    if (!details) return;
    if (details.open) {
      this.querySelectorAll(".mobile-facets__details[open]").forEach(
        (openDetails) => {
          if (openDetails !== details) this.closeSubmenu(openDetails);
        },
      );
    }
    details.classList.toggle("menu-opening", details.open);
    const main = details.closest(".mobile-facets__main");
    if (main)
      main.classList.toggle(
        "submenu-open",
        Boolean(main.querySelector(".mobile-facets__details[open]")),
      );
    details
      .querySelector(":scope > summary")
      ?.setAttribute("aria-expanded", details.open ? "true" : "false");
    if (details.open) {
      const submenu = details.querySelector(":scope > .mobile-facets__submenu");
      Array.from(
        submenu?.querySelectorAll(
          'button:not([disabled]), a[href], input:not([type="hidden"]):not([disabled]), select:not([disabled])',
        ) || [],
      )
        .find(isAvailableFacetFocusTarget)
        ?.focus();
    }
  }

  closeSubmenu(details, returnFocus = false) {
    if (!details) return;
    const summary = details.querySelector(":scope > summary");
    details.open = false;
    details.classList.remove("menu-opening");
    details.closest(".mobile-facets__main")?.classList.remove("submenu-open");
    summary?.setAttribute("aria-expanded", "false");
    if (returnFocus) summary?.focus();
  }

  close(returnFocus = false) {
    if (!this.details.open) return;
    const focusWasInside =
      this.contains(document.activeElement) &&
      document.activeElement !== this.summary;
    this.details.open = false;
    this.syncDrawerState(false);
    if (returnFocus || focusWasInside) this.summary.focus();
  }

  getFocusableElements() {
    const openSubmenu = this.querySelector(
      ".mobile-facets__details[open].menu-opening .mobile-facets__submenu",
    );
    const main = this.querySelector(".mobile-facets__main");

    return Array.from(
      this.details.querySelectorAll(
        'summary, a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled])',
      ),
    ).filter((element) => {
      if (element === this.summary || !isAvailableFacetFocusTarget(element))
        return false;
      const containingSubmenu = element.closest(".mobile-facets__submenu");
      if (containingSubmenu) return containingSubmenu === openSubmenu;
      return !openSubmenu || !main?.contains(element);
    });
  }

  onKeydown(event) {
    if (!this.details.open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      const submenu = event.target.closest(".mobile-facets__details[open]");
      if (submenu) {
        this.closeSubmenu(submenu, true);
      } else {
        this.close(true);
      }
      return;
    }

    if (event.key !== "Tab") return;
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("menu-drawer")) {
  customElements.define("menu-drawer", MobileFacetDrawer);
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this)),
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keydown", (event) => {
      if (event.key !== " ") return;
      event.preventDefault();
      this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
